import React, { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  getPropMixStats,
  retryPropMixLookup,
  retryAllPropMixFailures,
} from "../../store/modules/admin/propMixActions";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  CircularProgress,
  Alert,
  LinearProgress,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@mui/material";
import RefreshIcon from "@mui/icons-material/Refresh";
import RetryIcon from "@mui/icons-material/Replay";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ErrorIcon from "@mui/icons-material/Error";
import WarningIcon from "@mui/icons-material/Warning";

const PropMixDashboard: React.FC = () => {
  const queryClient = useQueryClient();
  const [error, setError] = useState<string | null>(null);
  const [retryDialog, setRetryDialog] = useState<{
    open: boolean;
    failureId: number | null;
    address: string;
  }>({ open: false, failureId: null, address: "" });

  // Fetch PropMix stats
  const { data: stats, isLoading: loading } = useQuery({
    queryKey: ["propMixStats"],
    queryFn: getPropMixStats,
    refetchInterval: 30000, // Auto-refresh every 30 seconds
  });

  // Retry single failure mutation
  const retryMutation = useMutation({
    mutationFn: (failureId: number) => retryPropMixLookup(failureId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["propMixStats"] });
      setRetryDialog({ open: false, failureId: null, address: "" });
    },
    onError: (err: any) => {
      setError(err.message || "Failed to retry lookup");
    },
  });

  // Retry all failures mutation
  const retryAllMutation = useMutation({
    mutationFn: () => retryAllPropMixFailures(),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["propMixStats"] });
    },
    onError: (err: any) => {
      setError(err.message || "Failed to retry all lookups");
    },
  });

  const loadData = () => {
    queryClient.invalidateQueries({ queryKey: ["propMixStats"] });
  };

  const handleRetry = (failureId: number) => {
    retryMutation.mutate(failureId);
  };

  const handleRetryAll = () => {
    retryAllMutation.mutate();
  };

  const openRetryDialog = (failureId: number, address: string) => {
    setRetryDialog({ open: true, failureId, address });
  };

  const closeRetryDialog = () => {
    setRetryDialog({ open: false, failureId: null, address: "" });
  };

  const retrying = retryMutation.isPending ? retryDialog.failureId : null;
  const retryingAll = retryAllMutation.isPending;

  if (loading && !stats) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="400px"
      >
        <CircularProgress />
      </Box>
    );
  }

  const dailyUsagePercent =
    stats?.todaysCallCount && stats?.dailyLimit
      ? (stats.todaysCallCount / stats.dailyLimit) * 100
      : 0;
  const monthlyUsagePercent =
    stats?.monthlyCallCount && stats?.monthlyLimit
      ? (stats.monthlyCallCount / stats.monthlyLimit) * 100
      : 0;

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        mb={3}
      >
        <Typography variant="h4">PropMix Admin Dashboard</Typography>
        <Button
          variant="outlined"
          startIcon={<RefreshIcon />}
          onClick={loadData}
          disabled={loading}
        >
          Refresh
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Stats Cards */}
      <Grid container spacing={3} mb={3}>
        {/* Daily Usage */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Daily Usage
              </Typography>
              <Typography variant="h3" color="primary" gutterBottom>
                {stats?.todaysCallCount || 0} / {stats?.dailyLimit || 0}
              </Typography>
              <LinearProgress
                variant="determinate"
                value={Math.min(dailyUsagePercent, 100)}
                sx={{ mb: 1, height: 8, borderRadius: 4 }}
                color={dailyUsagePercent > 90 ? "error" : "primary"}
              />
              <Typography variant="body2" color="text.secondary">
                {stats?.dailyRemaining || 0} calls remaining today
              </Typography>
              <Box mt={2} display="flex" gap={2}>
                <Chip
                  label={`${stats?.todaysFailedCount || 0} Failed`}
                  size="small"
                  color="error"
                  icon={<ErrorIcon />}
                />
                <Chip
                  label={`${stats?.todaysNoDataCount || 0} No Data`}
                  size="small"
                  color="warning"
                  icon={<WarningIcon />}
                />
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* Monthly Usage */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Monthly Usage
              </Typography>
              <Typography variant="h3" color="secondary" gutterBottom>
                {stats?.monthlyCallCount || 0} / {stats?.monthlyLimit || 0}
              </Typography>
              <LinearProgress
                variant="determinate"
                value={Math.min(monthlyUsagePercent, 100)}
                sx={{ mb: 1, height: 8, borderRadius: 4 }}
                color={monthlyUsagePercent > 90 ? "error" : "secondary"}
              />
              <Typography variant="body2" color="text.secondary">
                {stats?.monthlyRemaining || 0} calls remaining this month
              </Typography>
              <Box mt={2} display="flex" gap={2}>
                <Chip
                  label={`${stats?.monthlyFailedCount || 0} Failed`}
                  size="small"
                  color="error"
                  icon={<ErrorIcon />}
                />
                <Chip
                  label={`${stats?.monthlyNoDataCount || 0} No Data`}
                  size="small"
                  color="warning"
                  icon={<WarningIcon />}
                />
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Pending Failures */}
      <Card>
        <Box sx={{ borderBottom: 1, borderColor: "divider", p: 2 }}>
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
          >
            <Typography variant="h6">
              Pending Failures ({stats?.pendingFailures?.length || 0})
            </Typography>
            <Button
              variant="contained"
              startIcon={<RetryIcon />}
              onClick={handleRetryAll}
              disabled={
                retryingAll ||
                !stats?.pendingFailures ||
                stats.pendingFailures.length === 0
              }
            >
              {retryingAll ? "Retrying All..." : "Retry All"}
            </Button>
          </Box>
        </Box>

        <Box sx={{ p: 3 }}>
          {loading && (
            <Box display="flex" justifyContent="center" p={3}>
              <CircularProgress />
            </Box>
          )}

          {!loading &&
          stats?.pendingFailures &&
          stats.pendingFailures.length > 0 ? (
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Address</TableCell>
                    <TableCell>Transaction Date</TableCell>
                    <TableCell>Type</TableCell>
                    <TableCell>Price</TableCell>
                    <TableCell>Error</TableCell>
                    <TableCell>Retries</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell align="right">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {stats.pendingFailures.map((failure: any) => (
                    <TableRow key={failure.failureId}>
                      <TableCell>
                        <Typography variant="body2" fontWeight="medium">
                          {failure.streetAddress}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {failure.city}, {failure.state} {failure.postalCode}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        {failure.transactionDate
                          ? new Date(
                              failure.transactionDate
                            ).toLocaleDateString()
                          : "N/A"}
                      </TableCell>
                      <TableCell>{failure.transactionType || "N/A"}</TableCell>
                      <TableCell>
                        {failure.price
                          ? `$${failure.price.toLocaleString()}`
                          : "N/A"}
                      </TableCell>
                      <TableCell>
                        <Tooltip title={failure.errorMessage || ""}>
                          <Typography
                            variant="caption"
                            sx={{
                              maxWidth: 200,
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              whiteSpace: "nowrap",
                              display: "block",
                            }}
                          >
                            {failure.errorMessage}
                          </Typography>
                        </Tooltip>
                      </TableCell>
                      <TableCell>{failure.retryCount}</TableCell>
                      <TableCell>
                        <Chip
                          label={failure.failureType}
                          size="small"
                          color={
                            failure.failureType === "Error"
                              ? "error"
                              : "warning"
                          }
                        />
                      </TableCell>
                      <TableCell align="right">
                        <Tooltip title="Retry this lookup">
                          <IconButton
                            size="small"
                            onClick={() =>
                              openRetryDialog(
                                failure.failureId,
                                failure.streetAddress
                              )
                            }
                            disabled={retrying === failure.failureId}
                          >
                            {retrying === failure.failureId ? (
                              <CircularProgress size={20} />
                            ) : (
                              <RetryIcon />
                            )}
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          ) : (
            !loading && (
              <Alert severity="success" icon={<CheckCircleIcon />}>
                No pending failures! All PropMix lookups are up to date.
              </Alert>
            )
          )}
        </Box>
      </Card>

      {/* Retry Confirmation Dialog */}
      <Dialog open={retryDialog.open} onClose={closeRetryDialog}>
        <DialogTitle>Retry PropMix Lookup</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to retry the lookup for:
          </Typography>
          <Typography fontWeight="bold" mt={2}>
            {retryDialog.address}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeRetryDialog}>Cancel</Button>
          <Button
            onClick={() =>
              retryDialog.failureId && handleRetry(retryDialog.failureId)
            }
            variant="contained"
            disabled={retrying !== null}
          >
            {retrying ? "Retrying..." : "Retry"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default PropMixDashboard;
